import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";
import { ProgressIndicator } from "@fluentui/react/lib/ProgressIndicator";

import { selectNotifications } from "Store/Selectors/Notifications";
import { IWithTranslatorProps, WithTranslator } from "Components/Generic/Translator";
import { uiNotificationRemove } from "Store/Actions";
import { useNotificationStyles } from "./UseNotificationStyles";

export const Notification = WithTranslator((props: IWithTranslatorProps): JSX.Element => {
  const { translator } = props;
  const [remainingPercentPerMessageId, setRemainingPercentPerMessageId] = useState<{ [id: string]: number }>({});
  const [pausedMessageId, setPausedMessageId] = useState<number>(-1);
  const messages = useSelector(selectNotifications);
  const styles = useNotificationStyles();
  const dispatch = useDispatch();
  const visibleClass = messages.length > 0 ? styles.visible : ``;

  const hundredPercent = 1;
  const displayTimeInMs = 5 * 1000;
  const ticks = 40;
  const timePerTickInMs = displayTimeInMs / ticks;
  const percentPerTick = hundredPercent / ticks;

  function closeMessage(id: number): void {
    dispatch(uiNotificationRemove({ id }));
  }

  useEffect(() => {
    const id = setTimeout(() => {
      const percentStateCopy = { ...remainingPercentPerMessageId };

      for (const message of messages) {
        const isErrorOrPaused = message.type === `error` || message.id === pausedMessageId;
        if (isErrorOrPaused) {
          continue;
        }
        const percent = remainingPercentPerMessageId[message.id] ?? hundredPercent;
        if (percent <= 0) {
          closeMessage(message?.id);
          return;
        }
        percentStateCopy[message.id] = percent - percentPerTick;
      }
      setRemainingPercentPerMessageId(percentStateCopy);
    }, timePerTickInMs);

    return () => {
      clearInterval(id);
    };
  });

  return (
    <div className={`${styles.notificationWrap} ${visibleClass}`}>
      {messages.map((message) => {
        let messageTitle = `${translator.get(`notification.error`, `Error`)}!`;
        switch (message?.type) {
          case `success`:
            messageTitle = `${translator.get(`notification.success`, `Success`)}!`;
            break;
          case `warning`:
            messageTitle = `${translator.get(`notification.warning`, `Warning`)}!`;
            break;
          default:
            messageTitle = `${translator.get(`notification.error`, `Error`)}!`;
        }
        const messageType = MessageBarType[message?.type];

        return (
          <div
            onMouseEnter={() => setPausedMessageId(message.id)}
            onMouseLeave={() => setPausedMessageId(-1)}
            className={styles.relative}
            key={message?.id}
          >
            <MessageBar
              className={styles.messageBar}
              messageBarType={messageType}
              isMultiline
              onDismiss={() => closeMessage(message?.id)}
              dismissButtonAriaLabel="Close"
            >
              <b className={styles.title}>{messageTitle}</b>
              <span>
                {translator.get(
                  `notification.${message?.text}`,
                  () => `${translator.get(`notification.error`)}: ${message?.text}`
                )}
              </span>
            </MessageBar>
            {message.type !== `error`
              ? (
                <ProgressIndicator
                  className={styles.progressIndicator}
                  percentComplete={remainingPercentPerMessageId[message.id] ?? hundredPercent}
                />
              )
              : (
                ``
              )}
          </div>
        );
      })}
    </div>
  );
});
